import React, { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { BoxFull, Button, TransactionalSubheader } from "iuvity-ui-components";
import { NewCustomDialog, NewDialogProps } from "../../components/dialog/NewCustomDialog";
import { QuickLinksFormSchema } from "../../validations/schemas";
import { QUICKLINKS_TYPE_OPTIONS, REVALIDATE_FORM_PROPS, infoQuicklinksFields } from "../../constants/index";
import { AppContext } from "../../context";
import { useQuickLinks } from "../../hooks/useQuickLinks";
import { useVersions } from "../../hooks/useVersions";
import { useTranslations } from "../../hooks/useTranslations";
import { Version } from "../../interfaces";
import { useNavigate } from "react-router-dom";
import { Header } from '../../components/header/Header'
import { CustomFormComponent } from "../../components/shared/CustomFormComponent";
import { createLabelButtonMaker } from "../../helpers/createLabelButtonMaker";

interface NewQuickLinkProps {
  update?: boolean;
}

export const NewQuickLinkDefaultValues = {
  name: "",
  description: "",
  labelkey: "",
  type: "",
  linkto: "",
  isexternallink: false,
  issecuredurl: false,
  iconname: "",
  category: "",
  fieldorder: "",
  status: "INACTIVE",
  releaseversion: "",
  level: null,
  useimage: false,
  showlabel: true,
};

export const NewQuickLink: React.FC<NewQuickLinkProps> = ({ update = false }) => {
  const navigate = useNavigate()
  const {
    t,
    appState: { user, selectedQuickLink },
  } = useContext(AppContext);

  const { quickLinkAction, reloadFn } = useQuickLinks("DATA");
  const { data: versionsData } = useVersions("DATA");
  const { data: labelsData, reloadFn: reloadFnQuicklinksTranslations } = useTranslations("DATA");
  const [modalProps, setModalProps] = useState<NewDialogProps>({
    visible: false,
  } as NewDialogProps);

  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: update ?
      {
        id: selectedQuickLink?.id,
        name: selectedQuickLink?.name,
        description: selectedQuickLink?.description || '',
        labelkey: selectedQuickLink?.labelkey,
        type: selectedQuickLink?.type,
        iconname: selectedQuickLink?.iconname,
        linkto: selectedQuickLink?.linkto,
        isexternallink: selectedQuickLink?.isexternallink,
        issecuredurl: selectedQuickLink?.issecuredurl,
        fieldorder: selectedQuickLink?.fieldorder,
        category: selectedQuickLink?.category || null,
        status: selectedQuickLink?.status,
        releaseversion: selectedQuickLink?.releaseversion,
        level: selectedQuickLink?.level,
        useimage: selectedQuickLink?.useimage,
        showlabel: selectedQuickLink?.showlabel,
        updatedby: user?.name,
      } : { ...NewQuickLinkDefaultValues },
  });

  const {
    getValues,
    setValue,
    reset,
    formState: { isValid, isDirty },
  } = form;

  const formKeysOptions: any = {
    type: QUICKLINKS_TYPE_OPTIONS(t).sort(
      (a, b) => a.label.localeCompare(b.label)
    ),
    releaseversion: versionsData
      ?.map((ver: Version) => ({
        label: ver.name,
        value: ver.name,
      }))
      .sort((a: any, b: any) => b.label.localeCompare(a.label)),
    labelkey: labelsData?.length > 0
      ? (() => {
        let labelsOptions = labelsData?.filter((item: any) => item?.widgets)
          ?.map((item: any) => {
            const firstWidget = item.widgets[0];
            return {
              label: `${firstWidget}.${item.key}`,
              value: `${firstWidget}.${item.key}`
            };
          });
        return update ? (selectedQuickLink?.labelkey ? [...labelsOptions, { label: selectedQuickLink?.labelkey, value: selectedQuickLink?.labelkey }]:[...labelsOptions]) : labelsOptions;
      })()
        ?.sort((a: any, b: any) => a.label.localeCompare(b.label))
      : [],
  };

  const onReturn = () => {
    navigate(-1);
    window.scrollTo(0, 0);
  };

  const onCreateQuickLink = async () => {
    const formValues = getValues();
    formValues.category = formValues.category ? formValues.category : null
    formValues.level = formValues.level ? formValues.level : null

    const valuesToSend = Object.keys(formValues)
      .filter((key) => (formValues as any)[key] !== "")
      .reduce((cur, key) => {
        return Object.assign(cur, { [key]: (formValues as any)[key] });
      }, {});
    const actionStatus = await quickLinkAction(update ? "UPDATE" : "CREATE", update ?
      [formValues] :
      [
        {
          ...valuesToSend,
          createdby: user?.name,
        },
      ]);
    if (actionStatus === "SUCCESS") {
      reset(NewQuickLinkDefaultValues);
      reloadFn();
      navigate(-1);
      window.scrollTo(0, 0);
    }
  };

  const handleModalProps = (actionType: "action" | "cancel") => {
    switch (actionType) {
      case "action":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t(`Quicklinks.${update ? "save" : "create"}ModalTitle`),
          content: t(`Quicklinks.${update ? "save" : "create"}ModalContent`),
          onConfirm: onCreateQuickLink,
        }));
      case "cancel":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("Quicklinks.cancelModalTitle"),
          content: t("Quicklinks.cancelModalContent"),
          onConfirm: onReturn,
        }));
      default:
        return;
    }
  };

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}

      <TransactionalSubheader
        subheaderTitle={
          <div style={{ position: "relative", left: "-40px" }}>
            <i
              className="iu iu-icon-left pr-3"
              onClick={onReturn}
              aria-hidden="true"
              style={{
                fontSize: "var(--font-size-tablet-title)",
                cursor: "pointer",
              }}
            />
            {update ? t("Quicklinks.editTitle") : t("Quicklinks.newTitle")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={t("Quicklinks.breadcrumbParentLabel")}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("Quicklinks.quickLinksTitle"),
              command: () => onReturn(),
              style: { fontFamily: "var(--fontBarlowSemiBold)" },
            },
            {
              label: `${update ? t("Quicklinks.editTitle") : t("Quicklinks.newTitle")}`,
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
      </TransactionalSubheader>

      <CustomFormComponent
        functionality="Quicklinks"
        fields={infoQuicklinksFields}
        form={form}
        dropdownOptions={formKeysOptions}
        schemaOfValidation={QuickLinksFormSchema}
        REVALIDATE_FORM_PROPS={REVALIDATE_FORM_PROPS}
        dropdownFilterObject={{ labelkey: true }}
        dropdownShowClearObject={{ labelkey: false }}
        customComponent={createLabelButtonMaker({
          setModalProps,
          dialogTitle: t('ConsoleTranslations.createLabelTitle'),
          dialogDetail: t('ConsoleTranslations.createLabelDescription'),
          buttonLabel: t('ConsoleTranslations.createLabelTitle'),
          functionality: 'QuickLinks',
          actionsToExecute: (key: string, widget?: string) => {
            reloadFnQuicklinksTranslations();
            const labelKey = widget ? `${widget}.${key}` : key;
            setValue("labelkey", labelKey, REVALIDATE_FORM_PROPS)
          },
        })}
      />

      <BoxFull className="pt-0 px-4 pb-5" style={{ backgroundColor: "transparent" }}>
        <div className="flex align-items-center justify-content-end gap-5">
          <Button
            variant="p-button-link"
            label={t("Quicklinks.cancelBtnLabel")}
            onClick={isDirty ? () => handleModalProps("cancel") : onReturn}
          />
          <Button
            variant="p-button"
            label={
              update
                ? t("Quicklinks.saveBtnLabel")
                : t("Quicklinks.createBtnLabel")
            }
            disabled={
              update
                ?
                !(
                  (isDirty && isValid)
                )
                :
                !isValid
            }
            onClick={() => handleModalProps("action")}
          />
        </div>
      </BoxFull>

      <NewCustomDialog
        title={modalProps.title}
        content={modalProps.content}
        visible={modalProps.visible}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
        aditionalClass="new-quicklinks-page-dialog overlay-createlabelsbutton"
        acceptLabel={modalProps.acceptLabel}
        rejectLabel={modalProps.rejectLabel}
        onReject={modalProps.onReject}
      />
    </>
  );
};
